import type {
  CrossroadsLog,
  GroupMetadata,
} from '@/api/hooks/useIntegrationLogs'
import { SeverityTag } from '@/components/Display/SeverityDisplay'
import { friendlySagaName } from '@/constants/sagaTypes'
import { useSecurityContext } from '@/contexts/SecurityContext'
import { useTenantStore } from '@/stores/tenantStore'
import {
  CheckCircleOutlined,
  ExclamationCircleFilled,
  QuestionCircleOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { BBDTag, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import type { TabsProps } from 'antd'
import { Card, Table, Tabs, Tooltip } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { DebugTabContent } from './DebugTabContent'
import { SupplyContextCard } from './DetailCards'
import { InformationCard, isOrderChangeMetadata } from './LogInfoBoxes'
import { OrderChangeSummaryCard } from './OrderChangeSummary'
import { ISSUE_CATEGORY_LABELS, type V3LogDetail } from './categories'
import {
  type Solution,
  buildSolutionUrl,
  isUnownedSolution,
  ownsSolution,
  solutionOwnerLabel,
  tenantNamesFromRouting,
} from './solutionHandlers'
import { warningView } from './warnings'

const CONTAINER_STYLE = { flex: 2, width: '100%', overflowY: 'scroll' as const }
const CARD_STYLE = { marginBottom: 16 }
const ISSUE_CARD_STYLE = {
  backgroundColor: 'var(--theme-warning-dim)',
  border: '1px solid var(--theme-warning)',
}
const SUMMARY_CARD_STYLE = {
  backgroundColor: 'var(--theme-success-dim)',
  border: '1px solid var(--theme-success)',
}
const WARNING_CARD_STYLE = {
  backgroundColor: 'var(--theme-warning-dim)',
  border: '1px solid var(--theme-warning)',
}
const HEADER_ICON_OFFSET = { paddingTop: 4 }
const HEADER_TAGS_OFFSET = { paddingTop: 2 }
const SUCCESS_ICON_STYLE = { fontSize: '18px', color: 'var(--theme-success)' }
const ERROR_ICON_STYLE = { fontSize: '18px', color: 'var(--theme-error)' }
const WARNING_ICON_STYLE = { fontSize: '18px', color: 'var(--theme-warning)' }
const SOLUTIONS_CARD_STYLE = { backgroundColor: 'var(--bg-2)' }
// Nudged down to sit on the message's first line rather than its box top.
const EXTRA_INFO_ICON_STYLE = {
  color: 'var(--theme-text-hint)',
  fontSize: 13,
  paddingTop: 4,
  cursor: 'help',
}
const SOLUTION_LINK_STYLE = {
  color: 'hsl(var(--primary))',
  textDecoration: 'underline',
}
const OWNERSHIP_CARD_STYLE = {
  backgroundColor: 'hsl(var(--primary) / 0.12)',
  borderLeft: '3px solid hsl(var(--primary))',
}
const OWNERSHIP_ICON_STYLE = { fontSize: '18px', color: 'hsl(var(--primary))' }
const PRE_WRAP_STYLE = { whiteSpace: 'pre-wrap' as const }

export type V3LogDetailsPanelProps = {
  log: CrossroadsLog
  detail: V3LogDetail
  groupMetadata?: GroupMetadata | null
}

type SolutionRow = {
  key: number
  solution: Solution
  owner: string
  owned: boolean
}

function SolutionText({ row }: { row: SolutionRow }) {
  const url = row.owned ? buildSolutionUrl(row.solution) : null
  if (!url) {
    return (
      <Texto appearance={row.owned ? undefined : 'hint'}>
        {row.solution.message}
      </Texto>
    )
  }
  return (
    <a href={url} target='_blank' rel='noreferrer' style={SOLUTION_LINK_STYLE}>
      {row.solution.message}
    </a>
  )
}

function SolutionMessage({ row }: { row: SolutionRow }) {
  const extraInfo = row.solution.extra_info_message
  if (!extraInfo) return <SolutionText row={row} />
  return (
    <Horizontal gap={6} alignItems='flex-start'>
      <SolutionText row={row} />
      <Tooltip title={extraInfo}>
        <QuestionCircleOutlined style={EXTRA_INFO_ICON_STYLE} />
      </Tooltip>
    </Horizontal>
  )
}

const SOLUTION_COLUMNS: ColumnsType<SolutionRow> = [
  {
    title: 'Owner',
    dataIndex: 'owner',
    key: 'owner',
    width: 90,
    render: (owner: string, row) => (
      <Texto appearance={row.owned ? undefined : 'hint'} weight='600'>
        {owner}
      </Texto>
    ),
  },
  {
    title: 'Solution',
    key: 'solution',
    render: (_, row) => <SolutionMessage row={row} />,
  },
]

function V3DetailCard({
  log,
  detail,
}: {
  log: CrossroadsLog
  detail: V3LogDetail
}) {
  const { selectedTenant } = useTenantStore()
  const { hasGravitateAccess } = useSecurityContext()
  const sagaName = friendlySagaName(log.saga_type)
  const isSuccess = detail.category === 'success'
  const warnings = warningView(detail)

  let title = ISSUE_CATEGORY_LABELS[detail.category] ?? log.title
  let Icon = ExclamationCircleFilled
  let iconStyle = ERROR_ICON_STYLE
  if (detail.category === 'success') {
    const hasWarnings = warnings.length > 0
    // Each warning states its own sub-header below, so the heading stays generic --
    // a log can carry several, of more than one kind.
    title = hasWarnings ? 'Completed with Warnings' : 'Success'
    Icon = hasWarnings ? WarningOutlined : CheckCircleOutlined
    iconStyle = hasWarnings ? WARNING_ICON_STYLE : SUCCESS_ICON_STYLE
  }

  const supplyContext =
    detail.category === 'supply_v3' ? detail.supply_context : null

  const namesById = tenantNamesFromRouting(log.routing)
  const toSolutionRows = (solutions: Solution[]): SolutionRow[] =>
    solutions
      // An unowned solution is internal-only, and its Owner cell reads "-".
      .filter(
        (solution) => hasGravitateAccess() || !isUnownedSolution(solution),
      )
      .map((solution, i) => ({
        key: i,
        solution,
        owner: solutionOwnerLabel(solution, selectedTenant?.id, namesById),
        owned: ownsSolution(solution, selectedTenant?.id),
      }))

  const solutionRows = toSolutionRows([
    ...('solutions' in detail ? detail.solutions : []),
    ...warnings.flatMap((warning) => warning.solutions),
  ])
  const isOwner = solutionRows.some((row) => row.owned)

  return (
    <Card size='small' style={CARD_STYLE}>
      <Vertical gap={16}>
        <Horizontal
          gap={12}
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Icon style={{ ...iconStyle, ...HEADER_ICON_OFFSET }} />
          <Vertical gap={4} flex={1}>
            <Texto category='h5' weight='600'>
              {title}
            </Texto>
            {'subheader' in detail && <Texto>{detail.subheader}</Texto>}
          </Vertical>
          <Horizontal gap={8} alignItems='center' style={HEADER_TAGS_OFFSET}>
            <SeverityTag severity={log.severity} />
            {sagaName && <BBDTag>{sagaName}</BBDTag>}
          </Horizontal>
        </Horizontal>

        <Card
          size='small'
          style={isSuccess ? SUMMARY_CARD_STYLE : ISSUE_CARD_STYLE}
        >
          <Vertical gap={4}>
            {!isSuccess && (
              <Texto category='h5' weight='600'>
                Issue
              </Texto>
            )}
            <Texto style={PRE_WRAP_STYLE}>{detail.message}</Texto>
          </Vertical>
        </Card>

        {warnings.length > 0 && (
          <Vertical gap={8}>
            {warnings.map((warning, i) => (
              <Card key={i} size='small' style={WARNING_CARD_STYLE}>
                <Vertical gap={4}>
                  {(warning.subheader || warning.category) && (
                    <Horizontal
                      gap={8}
                      justifyContent='space-between'
                      alignItems='flex-start'
                    >
                      {warning.subheader && (
                        <Texto weight='600'>{warning.subheader}</Texto>
                      )}
                      {warning.category && <BBDTag>{warning.category}</BBDTag>}
                    </Horizontal>
                  )}
                  <Texto style={PRE_WRAP_STYLE}>{warning.message}</Texto>
                </Vertical>
              </Card>
            ))}
          </Vertical>
        )}

        {solutionRows.length > 0 && (
          <Card size='small' style={SOLUTIONS_CARD_STYLE}>
            <Vertical gap={8}>
              <Texto category='h5' weight='600'>
                Potential Solutions
              </Texto>
              <Table
                className='solutions-table'
                size='small'
                pagination={false}
                dataSource={solutionRows}
                columns={SOLUTION_COLUMNS}
              />
            </Vertical>
          </Card>
        )}

        {isOwner && (
          <Card size='small' style={OWNERSHIP_CARD_STYLE}>
            <Horizontal gap={12} alignItems='center'>
              <UserOutlined style={OWNERSHIP_ICON_STYLE} />
              <Texto weight='600'>You are an owner on this issue</Texto>
            </Horizontal>
          </Card>
        )}

        {supplyContext && supplyContext.length > 0 && (
          <SupplyContextCard drops={supplyContext} />
        )}
      </Vertical>
    </Card>
  )
}

export function V3LogDetailsPanel({
  log,
  detail,
  groupMetadata,
}: V3LogDetailsPanelProps) {
  const tabItems: TabsProps['items'] = useMemo(() => {
    return [
      {
        key: 'details',
        label: 'Details',
        children: (
          <Vertical className='mt-4'>
            <V3DetailCard log={log} detail={detail} />
            {isOrderChangeMetadata(log.metadata) && (
              <OrderChangeSummaryCard
                beforeChange={log.metadata.before_change}
                afterChange={log.metadata.after_change}
                timestamp={log.date}
              />
            )}
            <InformationCard log={log} groupMetadata={groupMetadata} />
          </Vertical>
        ),
      },
      {
        key: 'debug',
        label: 'Debug / Developer Info',
        children: <DebugTabContent log={log} />,
      },
    ]
  }, [log, detail, groupMetadata])

  return (
    <div style={CONTAINER_STYLE}>
      <Tabs defaultActiveKey='details' size='large' items={tabItems} />
    </div>
  )
}
